import {useEffect, useRef, useState} from "react";
import {useIntegrator} from "../../integrator.context";
import {AddTileCommand} from "../../commands/addTile.command";
import PromptDialog from "../../elements/promptDialog.element";
import LocalizationInput from "../../elements/localizationInput.element";
import {RemoveTileCommand} from "../../commands/removeTile.command";
import NumberInputLabel from "../../elements/numberInputLabel.element";
import {UpdateTileCommand} from "../../commands/updateTile.command";
import BooleanInputLabel from "../../elements/booleanInputLabel";
import EntityLabel from "../../elements/entityLabel";
import {TileData} from "../../models/remoteConstants.model";
import EntityDragLabel from "../../elements/entityDragLabel";
import {DropTargetMonitor, useDrop} from "react-dnd";
import Vector2Input from "../../elements/vector2.input";
import BooleanInput from "../../elements/booleanInput.element";
import IconButton from "../../elements/IconButton";
import SelectableLabel from "../../elements/selectable.label";
import ImagePackElement from "../../elements/imagePack.element";
import List from "../../elements/list";
import ResizeBar, {useResizable} from "../../elements/resizeBar";
import Collapsible from "../../elements/collapsible";
import {useApp} from "../../app.context";
import PaletteTabButton, {usePaletteTab} from "../../elements/paletteTabButton";

function TilesEditorView() {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue, navigateTo} = useApp();
    const [showPrompt, setShowPrompt] = useState(false);

    // Resizable palette: a vertical bar drags the width; tabs at the top
    // switch between the Resources and Buildings lists. See elements/resizeBar;
    // no collapse buttons here.
    const containerRef = useRef<HTMLDivElement>(null);
    const palette = useResizable({
        storageKey: 'rift.editor.tilesPaletteWidth',
        axis: 'x', min: 240, invert: true,                      // palette sits right of the bar
        initial: () => Math.round(window.innerWidth * 0.25),    // old fixed 25vw
        // Leave the tiles list at least this wide.
        max: () => (containerRef.current?.getBoundingClientRect().width ?? Infinity) - 320,
    });
    const [paletteTab, setPaletteTab] = usePaletteTab('rift.editor.tilesPaletteTab', 'Resources');

    return (
        <div ref={containerRef} className={'layout horizontal'} style={{flexGrow: 1}}>
            <div className={'layout vertical gap'} style={{gap: 10, flexGrow: 1, minWidth: 0}}>
                <List title={'Tiles'} elements={state.tiles}
                      headerClassName={'box'}
                      headerAction={<button style={{whiteSpace: 'nowrap'}} onClick={() => setShowPrompt(true)}>Add New Tile</button>}
                      component={element => <Tile tile={element}/>}
                      search={element => getLocalizationValue(element.id)}
                      keyFor={element => element.id}
                />
                {showPrompt && <PromptDialog
                    title={'New Tile'}
                    placeholder={'Enter tile name...'}
                    existingNames={state.tiles.map(t => getLocalizationValue(t.id))}
                    onConfirm={(name) => { const c = new AddTileCommand(name); executeCommand(c); if (c.newId) navigateTo({kind: 'tile', id: c.newId}); setShowPrompt(false); }}
                    onCancel={() => setShowPrompt(false)}
                />}
            </div>

            {/* Draggable bar to resize the palette width (drags horizontally). */}
            <ResizeBar axis={'x'} {...palette.handleProps}/>

            <div className={'layout vertical'} style={{width: palette.size, flexShrink: 0}}>
                <div className={'layout vertical gap box'} style={{height: 0, flexGrow: 1, overflowY: 'auto'}}>
                    <div className={'layout horizontal gap'} style={{width: '100%'}}>
                        <PaletteTabButton label={'Resources'} current={paletteTab} onClick={setPaletteTab}/>
                        <PaletteTabButton label={'Buildings'} current={paletteTab} onClick={setPaletteTab}/>
                    </div>
                    <div className={'layout vertical gap'} style={{flexGrow: 1, overflowY: 'auto'}}>
                        {paletteTab === 'Resources' && [...state.resources]
                            .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)))
                            .map(element => <div key={element.id}><EntityDragLabel id={element.id} type={'resource'} navTarget={{kind: 'resource', id: element.id}}/></div>)}
                        {paletteTab === 'Buildings' && [...state.buildings]
                            .sort((a, b) => getLocalizationValue(a.id).localeCompare(getLocalizationValue(b.id)))
                            .map(element => <div key={element.id}><EntityDragLabel id={element.id} type={'building'} level={element.startLevel} navTarget={{kind: 'building', id: element.id}}/></div>)}
                    </div>
                </div>
            </div>
        </div>
    )
}

function Tile(props: { tile: TileData }) {
    const {state, executeCommand} = useIntegrator();
    const {getLocalizationValue, confirmDelete, focusedTile} = useApp();

    // Ctrl/Cmd-click navigation target: open this tile's row and scroll it into
    // view when it becomes the focused tile (mirrors the unit/building rows).
    const [open, setOpen] = useState(false);
    const rowRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (focusedTile?.id === props.tile.id) {
            setOpen(true);
            rowRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
        }
    }, [focusedTile, props.tile.id]);

    function getTileTypeOptions(): string[] {
        const tiles = state.tiles.map(t => t.id);
        tiles.unshift('');
        return tiles;
    }

    function getSupportingTileOptions(): string[] {
        const otherTiles = state.tiles.filter(t => t.id !== props.tile.id).map(t => t.id);
        otherTiles.unshift('');
        return otherTiles;
    }

    function checkCanDrop(monitor: DropTargetMonitor<any, void>): boolean {
        if (!monitor.isOver({shallow: true})) {
            return false;
        }

        if (props.tile === undefined) {
            return false;
        }

        const type = monitor.getItemType();
        const item = monitor.getItem();
        switch (type) {
            case 'resource':
                return !props.tile.resources?.includes(item.id);
            case 'building':
                return !props.tile.buildings?.includes(item.id);
            default:
                return false;
        }
    }

    const [{canDrop}, drop] = useDrop({
        accept: ['resource', 'building'],
        drop: (item: any, monitor) => {
            if (props.tile === undefined || !checkCanDrop(monitor)) {
                return;
            }

            switch (monitor.getItemType()) {
                case 'resource':
                    props.tile.resources ??= [];
                    props.tile.resources.push(item.id);
                    break;
                case 'building':
                    props.tile.buildings ??= [];
                    props.tile.buildings.push(item.id);
                    break;
                default:
                    return;
            }

            executeCommand(new UpdateTileCommand(props.tile));
        },
        collect: (monitor) => {
            return {
                canDrop: checkCanDrop(monitor)
            };
        },
    });

    const header = (
        <div className={'layout horizontal'} style={{gap: 10, alignItems: 'center'}}>
            <ImagePackElement id={props.tile.id} level={0} size={80}/>
            <LocalizationInput id={props.tile.id} style={{width: 100}}/>
            <BooleanInput value={props.tile.active} onChange={(value) => {
                props.tile.active = value;
                executeCommand(new UpdateTileCommand(props.tile));
            }}/>
            <IconButton icon={'trash'} onClick={async (e) => {
                e.stopPropagation();
                if (await confirmDelete(getLocalizationValue(props.tile.id), 'tile'))
                    executeCommand(new RemoveTileCommand(props.tile.id));
            }}/>
        </div>
    );

    return (
        <div ref={(el) => { drop(el); rowRef.current = el; }} className={'box transition'} style={{
            opacity: props.tile.active ? 1 : 0.5,
            border: `solid 1px ${canDrop ? 'var(--tint-color)' : 'var(--overlay-color)'}`}}>
            <Collapsible title={header} defaultOpen={false} open={open} onOpenChange={setOpen}>
                <div className={'layout horizontal gap'} style={{gap: 20, marginTop: 10}}>
                    <div className={'layout vertical flex-grow'} style={{gap: 10}}>
                        <div className={'layout vertical gap'}
                             style={{borderRadius: '5px', flexGrow: 1}}>
                            <p className={'text header'}>Resources, Buildings</p>
                            {props.tile.resources && props.tile.resources.map((resource, i) => {
                                return <EntityLabel key={i} id={resource} navTarget={{kind: 'resource', id: resource}} onDelete={() => {
                                    props.tile.resources = props.tile.resources.filter(r => r !== resource);
                                    executeCommand(new UpdateTileCommand(props.tile));
                                }}/>
                            })}
                            {props.tile.buildings && props.tile.buildings.map((building, i) => {
                                const buildingData = state.buildings.find(b => b.id === building);
                                return <EntityLabel key={i} id={building} level={buildingData?.startLevel} navTarget={{kind: 'building', id: building}} onDelete={() => {
                                    props.tile.buildings = props.tile.buildings.filter(r => r !== building);
                                    executeCommand(new UpdateTileCommand(props.tile));
                                }}/>
                            })}
                        </div>
                    </div>
                    <div className={'layout vertical gap flex-grow'} style={{gap: 20}}>
                        <p className={'text header'}>World Generation</p>
                        <div className={'layout vertical gap'}>
                            <NumberInputLabel field={'tile.rarity'} value={props.tile.rarity}
                                              style={{flexGrow: 'unset'}}
                                              onChange={(value) => {
                                                  props.tile.rarity = value;
                                                  executeCommand(new UpdateTileCommand(props.tile));
                                              }}/>
                            <Vector2Input field={'tile.elevationNoise'} value={props.tile.elevationNoise}
                                          onChange={(value) => {
                                              props.tile.elevationNoise = value;
                                              executeCommand(new UpdateTileCommand(props.tile));
                                          }}/>
                            <Vector2Input field={'tile.temperatureNoise'} value={props.tile.temperatureNoise}
                                          onChange={(value) => {
                                              props.tile.temperatureNoise = value;
                                              executeCommand(new UpdateTileCommand(props.tile));
                                          }}/>
                        </div>
                        <p className={'text header'}>Statistics</p>
                        <div className={'layout vertical gap'}>
                            <NumberInputLabel field={'tile.additionalVisionRange'} value={props.tile.additionalVisionRange}
                                              style={{flexGrow: 'unset'}}
                                              onChange={(value) => {
                                                  props.tile.additionalVisionRange = value;
                                                  executeCommand(new UpdateTileCommand(props.tile));
                                              }}/>
                            <NumberInputLabel field={'tile.movementPrice'} value={props.tile.movementPrice}
                                              style={{flexGrow: 'unset'}}
                                              onChange={(value) => {
                                                  props.tile.movementPrice = value;
                                                  executeCommand(new UpdateTileCommand(props.tile));
                                              }}/>
                            {/* Fractional (the design's road cost is 0.5), and absent from
                                branches authored before the field existed — hence the ?? default,
                                which matches the client's own. */}
                            <NumberInputLabel field={'tile.roadMovementPrice'} value={props.tile.roadMovementPrice ?? 0.5}
                                              style={{flexGrow: 'unset'}} displayDecimals={2}
                                              onChange={(value) => {
                                                  props.tile.roadMovementPrice = value;
                                                  executeCommand(new UpdateTileCommand(props.tile));
                                              }}/>
                            {/* Marks Trail terrain (mountains): a road here dies with its
                                builder and can then be claimed by a survivor. */}
                            <BooleanInputLabel field={'tile.roadIsTrail'} value={props.tile.roadIsTrail ?? false} onChange={value => {
                                props.tile.roadIsTrail = value;
                                executeCommand(new UpdateTileCommand(props.tile));
                            }}/>
                            <SelectableLabel field={'tile.tileType'} value={props.tile.tileType}
                                options={getTileTypeOptions()} onChange={value => {
                                    props.tile.tileType = value;
                                executeCommand(new UpdateTileCommand(props.tile));
                            }}/>
                            <SelectableLabel field={'tile.supportingTile'} value={props.tile.supportingTile}
                                options={getSupportingTileOptions()} onChange={value => {
                                props.tile.supportingTile = value;
                                executeCommand(new UpdateTileCommand(props.tile));
                            }}/>
                            <BooleanInputLabel field={'tile.canMove'} value={props.tile.canMove} onChange={value => {
                                props.tile.canMove = value;
                                executeCommand(new UpdateTileCommand(props.tile));
                            }}/>
                            <BooleanInputLabel field={'tile.canSettle'} value={props.tile.canSettle} onChange={value => {
                                props.tile.canSettle = value;
                                executeCommand(new UpdateTileCommand(props.tile));
                            }}/>
                            <BooleanInputLabel field={'tile.canSail'} value={props.tile.canSail} onChange={value => {
                                props.tile.canSail = value;
                                executeCommand(new UpdateTileCommand(props.tile));
                            }}/>
                            <BooleanInputLabel field={'tile.canHide'} value={props.tile.canHide} onChange={value => {
                                props.tile.canHide = value;
                                executeCommand(new UpdateTileCommand(props.tile));
                            }}/>
                            <BooleanInputLabel field={'tile.canFortify'} value={props.tile.canFortify} onChange={value => {
                                props.tile.canFortify = value;
                                executeCommand(new UpdateTileCommand(props.tile));
                            }}/>
                        </div>
                    </div>
                </div>
            </Collapsible>
        </div>
    )
}

export default TilesEditorView;